import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiConfig } from '../authConfig';

export function AppShell({ children }: { children: ReactNode }) {
  const { userInfo, logout } = useAuth();
  const location = useLocation();
  // AppShell is rendered outside the matched <Route>, so useParams() returns
  // empty here. Parse the pathname directly to expose industry/schema context
  // to the sidebar.
  const match = /^\/industries\/([^/]+)(?:\/schemas\/([^/]+))?/.exec(location.pathname);
  const industryId = match?.[1];
  const schemaId = match?.[2];

  const isOn = (prefix: string) => location.pathname === prefix || location.pathname.startsWith(prefix + '/');

  const showAdmin = Boolean(
    userInfo?.platformAdmin ||
      userInfo?.allowedIndustries?.some((r) => r.role === 'owner'),
  );

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__brand">
          <img src="/logo.png" alt={apiConfig.tenantDisplayName} style={{ height: 28, marginRight: 8 }} />
          <span style={{ fontWeight: 600 }}>{apiConfig.tenantDisplayName}</span>
          <span className="app-header__product">QR Platform</span>
        </div>
        <div className="app-header__user">
          <span className="app-header__email">{userInfo?.email}</span>
          <button className="btn btn--ghost" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </header>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <nav
          style={{
            width: 240,
            borderRight: '1px solid var(--border-color)',
            background: 'var(--card-bg)',
            padding: '1rem 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <SideHeading>Programs</SideHeading>
          <SideLink to="/industries" active={isOn('/industries') && !industryId}>
            All programs
          </SideLink>
          {industryId && (
            <>
              <SideLink
                to={`/industries/${industryId}/schemas`}
                active={isOn(`/industries/${industryId}/schemas`) && !schemaId}
              >
                &nbsp;&nbsp;Schemas
              </SideLink>
              {schemaId && schemaId !== 'new' && (
                <>
                  <SideLink
                    to={`/industries/${industryId}/schemas/${schemaId}`}
                    active={location.pathname === `/industries/${industryId}/schemas/${schemaId}`}
                  >
                    &nbsp;&nbsp;&nbsp;&nbsp;Edit schema
                  </SideLink>
                  <SideLink
                    to={`/industries/${industryId}/schemas/${schemaId}/participants`}
                    active={isOn(`/industries/${industryId}/schemas/${schemaId}/participants`)}
                  >
                    &nbsp;&nbsp;&nbsp;&nbsp;Participants
                  </SideLink>
                  <SideLink
                    to={`/industries/${industryId}/schemas/${schemaId}/generate`}
                    active={isOn(`/industries/${industryId}/schemas/${schemaId}/generate`)}
                  >
                    &nbsp;&nbsp;&nbsp;&nbsp;Generate QR
                  </SideLink>
                  <SideLink
                    to={`/industries/${industryId}/schemas/${schemaId}/batch`}
                    active={isOn(`/industries/${industryId}/schemas/${schemaId}/batch`)}
                  >
                    &nbsp;&nbsp;&nbsp;&nbsp;Batch QR
                  </SideLink>
                </>
              )}
              {userInfo?.platformAdmin && (
                <SideLink
                  to={`/industries/${industryId}/audit`}
                  active={isOn(`/industries/${industryId}/audit`)}
                >
                  &nbsp;&nbsp;Audit log
                </SideLink>
              )}
            </>
          )}
          {showAdmin && (
            <>
              <SideHeading>Admin</SideHeading>
              <SideLink to="/admin/users" active={isOn('/admin/users')}>
                Users
              </SideLink>
            </>
          )}
        </nav>
        <main style={{ flex: 1, overflow: 'auto', padding: '1.5rem 2rem' }}>{children}</main>
      </div>
    </div>
  );
}

function SideHeading({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: '0.75rem 1.25rem 0.25rem',
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--text-muted)',
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

function SideLink({ to, active, children }: { to: string; active: boolean; children: ReactNode }) {
  return (
    <Link
      to={to}
      style={{
        display: 'block',
        padding: '0.5rem 1.25rem',
        textDecoration: 'none',
        color: active ? 'var(--primary-color)' : 'var(--text-color)',
        background: active ? 'rgba(29, 78, 216, 0.08)' : 'transparent',
        fontWeight: active ? 600 : 400,
        fontSize: '0.9rem',
      }}
    >
      {children}
    </Link>
  );
}
